"use client";

import { useState, useEffect } from "react";

export function ContactForm({ formData, updateFormData }) {
  const [fullName, setFullName] = useState(formData.fullName || "");
  const [email, setEmail] = useState(formData.email || "");
  const [state, setState] = useState(formData.state || "");
  const [addressLine1, setAddressLine1] = useState(formData.addressLine1 || "");
  const [addressLine2, setAddressLine2] = useState(formData.addressLine2 || "");
  const [phoneNumber, setPhoneNumber] = useState(formData.phoneNumber || "");

  // Validation states
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhoneNumber = (phone) => {
    // More flexible phone validation - accepts various formats
    const phoneRegex = /^[+]?[(]?[\d\s\-$$$$]{10,}$/;
    return (
      phoneRegex.test(phone.replace(/[\s\-$$$$]/g, "")) &&
      phone.replace(/[\s\-$$$$]/g, "").length >= 10
    );
  };

  const validateField = (fieldName, value) => {
    let error = "";

    switch (fieldName) {
      case "fullName":
        if (!value.trim()) {
          error = "Full name is required";
        } else if (value.trim().length < 2) {
          error = "Full name must be at least 2 characters";
        } else if (!/^[a-zA-Z\s]+$/.test(value.trim())) {
          error = "Full name should only contain letters and spaces";
        }
        break;
      case "email":
        if (!value.trim()) {
          error = "Email address is required";
        } else if (!validateEmail(value)) {
          error = "Please enter a valid email address";
        }
        break;
      case "phoneNumber":
        if (!value.trim()) {
          error = "Phone number is required";
        } else if (!validatePhoneNumber(value)) {
          error = "Please enter a valid phone number (at least 10 digits)";
        }
        break;
      case "addressLine1":
        if (!value.trim()) {
          error = "Address is required";
        } else if (value.trim().length < 5) {
          error = "Please enter a complete address";
        }
        break;
      case "state":
        if (!value.trim()) {
          error = "State is required";
        } else if (value.trim().length < 2) {
          error = "Please enter a valid state";
        }
        break;
      // addressLine2 is optional, no validation needed
      default:
        break;
    }

    return error;
  };

  const handleFieldChange = (fieldName, value) => {
    // Update the field value
    switch (fieldName) {
      case "fullName":
        setFullName(value);
        break;
      case "email":
        setEmail(value);
        break;
      case "state":
        setState(value);
        break;
      case "addressLine1":
        setAddressLine1(value);
        break;
      case "addressLine2":
        setAddressLine2(value);
        break;
      case "phoneNumber":
        setPhoneNumber(value);
        break;
    }

    // Clear error when user starts typing
    if (errors[fieldName]) {
      setErrors((prev) => ({
        ...prev,
        [fieldName]: "",
      }));
    }
  };

  const handleBlur = (fieldName) => {
    setTouched((prev) => ({
      ...prev,
      [fieldName]: true,
    }));

    const value = {
      fullName,
      email,
      state,
      addressLine1,
      addressLine2,
      phoneNumber,
    }[fieldName];

    const error = validateField(fieldName, value);
    setErrors((prev) => ({
      ...prev,
      [fieldName]: error,
    }));
  };

  const validateAllFields = () => {
    const newErrors = {};
    const requiredFields = [
      "fullName",
      "email",
      "phoneNumber",
      "addressLine1",
      "state",
    ];

    requiredFields.forEach((field) => {
      const value = {
        fullName,
        email,
        state,
        addressLine1,
        phoneNumber,
      }[field];

      const error = validateField(field, value);
      if (error) {
        newErrors[field] = error;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    // Mark all required fields as touched
    const requiredFields = [
      "fullName",
      "email",
      "phoneNumber",
      "addressLine1",
      "state",
    ];
    const newTouched = {};
    requiredFields.forEach((field) => {
      newTouched[field] = true;
    });
    setTouched(newTouched);

    // Validate all fields
    const isValid = validateAllFields();

    // Always update form data (parent component will handle validation)
    updateFormData({
      fullName: fullName.trim(),
      email: email.trim(),
      state: state.trim(),
      addressLine1: addressLine1.trim(),
      addressLine2: addressLine2.trim(),
      phoneNumber: phoneNumber.trim(),
      isContactFormValid: isValid,
    });

    return isValid;
  };

  // Update form data whenever fields change
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      updateFormData({
        fullName: fullName.trim(),
        email: email.trim(),
        state: state.trim(),
        addressLine1: addressLine1.trim(),
        addressLine2: addressLine2.trim(),
        phoneNumber: phoneNumber.trim(),
        isContactFormValid: validateAllFields(),
      });
    }, 300); // Debounce updates

    return () => clearTimeout(timeoutId);
  }, [fullName, email, state, addressLine1, addressLine2, phoneNumber]);

  // Consistent styling classes
  const inputClassName =
    "w-full p-4 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#7B189F] focus:border-[#7B189F] transition-colors";
  const errorInputClassName =
    "w-full p-4 border border-red-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors";

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-medium font-fraunces">
          Add your contact details
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          These details will be visible to potential buyers who are interested
          in your listing.
        </p>
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <label
            htmlFor="fullName"
            className="block text-sm font-medium text-gray-700"
          >
            Full Name <span className="text-red-500">*</span>
          </label>
          <input
            id="fullName"
            type="text"
            placeholder="Enter your full name"
            value={fullName}
            onChange={(e) => handleFieldChange("fullName", e.target.value)}
            onBlur={() => handleBlur("fullName")}
            className={
              touched.fullName && errors.fullName
                ? errorInputClassName
                : inputClassName
            }
            required
          />
          {touched.fullName && errors.fullName && (
            <p className="text-red-500 text-sm mt-1">{errors.fullName}</p>
          )}
        </div>

        <div className="space-y-2">
          <label
            htmlFor="email"
            className="block text-sm font-medium text-gray-700"
          >
            Email Address <span className="text-red-500">*</span>
          </label>
          <input
            id="email"
            type="email"
            placeholder="Enter your email address"
            value={email}
            onChange={(e) => handleFieldChange("email", e.target.value)}
            onBlur={() => handleBlur("email")}
            className={
              touched.email && errors.email
                ? errorInputClassName
                : inputClassName
            }
            required
          />
          {touched.email && errors.email && (
            <p className="text-red-500 text-sm mt-1">{errors.email}</p>
          )}
        </div>

        <div className="space-y-2">
          <label
            htmlFor="addressLine1"
            className="block text-sm font-medium text-gray-700"
          >
            Address Line 1 <span className="text-red-500">*</span>
          </label>
          <input
            id="addressLine1"
            type="text"
            placeholder="Enter your street address"
            value={addressLine1}
            onChange={(e) => handleFieldChange("addressLine1", e.target.value)}
            onBlur={() => handleBlur("addressLine1")}
            className={
              touched.addressLine1 && errors.addressLine1
                ? errorInputClassName
                : inputClassName
            }
            required
          />
          {touched.addressLine1 && errors.addressLine1 && (
            <p className="text-red-500 text-sm mt-1">{errors.addressLine1}</p>
          )}
        </div>

        <div className="space-y-2">
          <label
            htmlFor="addressLine2"
            className="block text-sm font-medium text-gray-700"
          >
            Address Line 2
          </label>
          <input
            id="addressLine2"
            type="text"
            placeholder="Apartment, suite, unit, etc. (optional)"
            value={addressLine2}
            onChange={(e) => handleFieldChange("addressLine2", e.target.value)}
            className={inputClassName}
          />
        </div>

        <div className="space-y-2">
          <label
            htmlFor="state"
            className="block text-sm font-medium text-gray-700"
          >
            State <span className="text-red-500">*</span>
          </label>
          <input
            id="state"
            type="text"
            placeholder="Enter state"
            value={state}
            onChange={(e) => handleFieldChange("state", e.target.value)}
            onBlur={() => handleBlur("state")}
            className={
              touched.state && errors.state
                ? errorInputClassName
                : inputClassName
            }
            required
          />
          {touched.state && errors.state && (
            <p className="text-red-500 text-sm mt-1">{errors.state}</p>
          )}
        </div>

        <div className="space-y-2">
          <label
            htmlFor="phoneNumber"
            className="block text-sm font-medium text-gray-700"
          >
            Phone Number <span className="text-red-500">*</span>
          </label>
          <input
            id="phoneNumber"
            type="tel"
            placeholder="Enter your phone number"
            value={phoneNumber}
            onChange={(e) => handleFieldChange("phoneNumber", e.target.value)}
            onBlur={() => handleBlur("phoneNumber")}
            className={
              touched.phoneNumber && errors.phoneNumber
                ? errorInputClassName
                : inputClassName
            }
            required
          />
          {touched.phoneNumber && errors.phoneNumber && (
            <p className="text-red-500 text-sm mt-1">{errors.phoneNumber}</p>
          )}
        </div>
      </div>

      {/* Validation Summary */}
      {Object.keys(errors).some((key) => errors[key] && touched[key]) && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-red-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Please fix the following errors:
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <ul className="list-disc pl-5 space-y-1">
                  {Object.keys(errors).map(
                    (key) =>
                      errors[key] &&
                      touched[key] && (
                        <li key={key}>
                          {key === "fullName" && "Full Name: "}
                          {key === "email" && "Email: "}
                          {key === "phoneNumber" && "Phone Number: "}
                          {key === "addressLine1" && "Address: "}
                          {key === "state" && "State: "}
                          {errors[key]}
                        </li>
                      )
                  )}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
